import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const SHAPE_PRESET_COLORS = [
  "#1C1B17",
  "#FFFFFF",
  "#F7F7F5",
  "#EFE9DF",
  "#C27A62",
  "#8A9A86",
  "#806244",
  "#525252",
  "#E11D48",
  "#0EA5E9",
  "#F59E0B",
  "#10B981",
];

export default function MoodboardShapeStyleDialog({
  open,
  onOpenChange,
  item,
  onChange,
}) {
  if (!item || item.kind !== "shape") return null;

  function set(patch) {
    onChange(patch);
  }

  const isLine = item.shape === "line";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid="shape-style-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isLine ? "Line style" : "Shape style"}
          </DialogTitle>
          <DialogDescription className="text-sm text-stone-500">
            Adjust the appearance of the selected {item.shape}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Fill (not for lines) */}
          {!isLine && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
                Fill
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                {SHAPE_PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => set({ fill: c })}
                    data-testid={`shape-fill-preset-${c.replace("#", "")}`}
                    className={`w-7 h-7 rounded-full border transition-transform ${
                      (item.fill || "#C27A62").toUpperCase() === c.toUpperCase()
                        ? "ring-2 ring-stone-900 scale-110"
                        : "border-stone-200 hover:scale-105"
                    }`}
                    style={{ background: c }}
                    aria-label={`fill ${c}`}
                  />
                ))}
                <label
                  className="flex items-center gap-1.5 bg-stone-100 h-8 px-2.5 rounded-full cursor-pointer"
                  title="Custom fill"
                >
                  <span className="text-[10px] uppercase tracking-[0.15em] text-stone-500">
                    Custom
                  </span>
                  <input
                    type="color"
                    value={item.fill || "#C27A62"}
                    onChange={(e) => set({ fill: e.target.value })}
                    data-testid="shape-fill-input"
                    className="w-6 h-6 rounded-full border-none bg-transparent cursor-pointer p-0"
                  />
                </label>
              </div>
            </div>
          )}

          {/* Stroke */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              {isLine ? "Color" : "Border colour"}
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              {SHAPE_PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => set({ stroke: c })}
                  data-testid={`shape-stroke-preset-${c.replace("#", "")}`}
                  className={`w-6 h-6 rounded-full border transition-transform ${
                    (item.stroke || "#1c1b17").toUpperCase() === c.toUpperCase()
                      ? "ring-2 ring-stone-900 scale-110"
                      : "border-stone-200 hover:scale-105"
                  }`}
                  style={{ background: c }}
                  aria-label={`stroke ${c}`}
                />
              ))}
              <label
                className="flex items-center gap-1.5 bg-stone-100 h-7 px-2 rounded-full cursor-pointer"
                title="Custom border"
              >
                <input
                  type="color"
                  value={item.stroke || "#1c1b17"}
                  onChange={(e) => set({ stroke: e.target.value })}
                  data-testid="shape-stroke-input"
                  className="w-5 h-5 rounded-full border-none bg-transparent cursor-pointer p-0"
                />
              </label>
            </div>
          </div>

          {/* Stroke width */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              {isLine ? "Thickness (px)" : "Border width (px)"}
            </label>
            <Input
              type="number"
              min={0}
              max={40}
              step={1}
              value={item.stroke_width ?? (isLine ? 4 : 0)}
              onChange={(e) =>
                set({ stroke_width: Number(e.target.value) || 0 })
              }
              data-testid="shape-stroke-width-input"
              className="h-10"
            />
          </div>

          {/* Corner radius (rectangle only) */}
          {item.shape === "rectangle" && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
                Corner radius (px)
              </label>
              <Input
                type="number"
                min={0}
                max={200}
                step={1}
                value={item.corner_radius ?? 0}
                onChange={(e) =>
                  set({ corner_radius: Number(e.target.value) || 0 })
                }
                data-testid="shape-corner-radius-input"
                className="h-10"
              />
            </div>
          )}

          {/* Opacity */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              Opacity ({Math.round((item.opacity ?? 1) * 100)}%)
            </label>
            <input
              type="range"
              min={10}
              max={100}
              step={5}
              value={Math.round((item.opacity ?? 1) * 100)}
              onChange={(e) =>
                set({ opacity: Number(e.target.value) / 100 })
              }
              data-testid="shape-opacity-input"
              className="w-full"
            />
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={() => onOpenChange(false)}
            data-testid="shape-style-done"
            className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
